/**
 * 3D Model Uploader and Estimator Component
 * Dynamically loads Three.js and parses STL/OBJ files to compute volume,
 * dimensions, and dynamically update the Shopify product form quantity.
 */

// ---------------------------------------------------------------------------
// Quote model
//
// A print is costed the way a print shop actually costs one, not as a flat rate
// per cm3 of bounding volume:
//
//   setup     - per part: slicing, plate prep, part removal, support cleanup, QC
//   material  - the plastic actually extruded, priced BY WEIGHT, because that is
//               how filament is bought. Infill decides how much that is, but
//               perimeters and the solid top/bottom layers mean even a 0% infill
//               part still uses ~SHELL_FRACTION of its volume in plastic.
//   machine   - hours on the printer. These track total volume and layer height,
//               NOT how much plastic is inside: a hollow part still has to trace
//               every perimeter on every layer.
//
// Defaults are FDM/PLA in INR and are all merchant-editable in the theme editor.
// They are defensible starting points, not shop policy.
// ---------------------------------------------------------------------------

// Fraction of a part that is solid plastic no matter what infill is chosen
// (perimeters + top/bottom shells). Typical for 3 perimeters and 4 shell layers.
const SHELL_FRACTION = 0.18;

// Machine hours scale with layer height, so finer quality costs more time per cm3.
// Roughly inverse to layer height: 0.20mm / 0.15mm / 0.10mm.
const QUALITY_TIME_FACTOR = { 'Standard': 1.0, 'High': 1.35, 'Ultra': 2.0 };

// A part whose largest dimension is under this almost certainly is not in mm.
const SUSPICIOUS_MAX_DIM_MM = 5;

// STL/OBJ carry no units; slicers assume mm by convention.
const UNIT_SCALE = { mm: 1, cm: 10, inch: 25.4 };

// Resize limits, as a percentage of the file's own size. 100 = as uploaded.
const SCALE_MIN_PCT = 10;
const SCALE_MAX_PCT = 400;
const SCALE_STEP_PCT = 5;

function numSetting(raw, fallback) {
  const n = parseFloat(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

class ThreeDUploader extends HTMLElement {
  constructor() {
    super();
    this.threeLoaded = false;
    this.THREE = null;
    this.OrbitControls = null;
    this.STLLoader = null;
    this.OBJLoader = null;
    
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.gridHelper = null;
    this.axesHelper = null;
    this.buildVolumeBox = null;
    this.animationId = null;

    this.modelFiles = [];
    this.activeModelIndex = 0;
    this.displayUnit = 'mm'; // 'mm' or 'inch'
    this.activeViewModes = {
      grid: true,
      axes: true,
      wireframe: false
    };

    this.couponCode = '';
    this.discountPercent = 0;
  }

  connectedCallback() {
    if (this.querySelector('input[type="file"]')) {
      this.initUploader();
    } else {
      setTimeout(() => this.initUploader(), 0);
    }
  }

  initUploader() {
    this.setupElements();
    this.generateOrderId();
    this.bindEvents();
  }

  disconnectedCallback() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.renderer) {
      this.renderer.dispose();
    }
    window.removeEventListener('resize', this.onResize);
  }

  setupElements() {
    // Inputs & zones
    this.fileInput = this.querySelector('input[type="file"]');
    this.dropzone = this.querySelector('.three-d-uploader__dropzone');
    this.viewerContainer = this.querySelector('.three-d-uploader__viewer');
    this.canvasContainer = this.querySelector('.three-d-uploader__canvas-container');
    this.canvas = this.querySelector('canvas');
    this.loaderElement = this.querySelector('.three-d-uploader__loader');
    this.errorElement = this.querySelector('.three-d-uploader__error-message');
    
    // View mode and Unit controls
    this.viewModeButtons = this.querySelectorAll('[data-view-mode]');
    this.unitButtons = this.querySelectorAll('[data-unit]');
    this.resetViewBtn = this.querySelector('.viewer-reset-btn');
    this.addMoreBtn = this.querySelector('.add-more-btn');

    // Viewer stats elements
    this.viewerFilename = this.querySelector('[data-viewer="filename"]');
    this.viewerDimensions = this.querySelector('[data-viewer="dimensions"]');
    this.viewerTriangles = this.querySelector('[data-viewer="triangles"]');
    this.viewerVolume = this.querySelector('[data-viewer="volume"]');

    // Pagination elements
    this.prevBtn = this.querySelector('[data-paginate="prev"]');
    this.nextBtn = this.querySelector('[data-paginate="next"]');
    this.pageNumbersContainer = this.querySelector('.pagination-numbers');

    // Step 2 Customize Table
    this.tableBody = this.querySelector('#CustomizeTableBody');

    // Step 3 Sidebar Elements
    this.orderIdEl = this.querySelector('#SidebarOrderId');
    this.totalFilesEl = this.querySelector('#SidebarTotalFiles');
    this.leadTimeEl = this.querySelector('#SidebarLeadTime');
    this.subtotalEl = this.querySelector('#SidebarSubtotal');
    this.gstEl = this.querySelector('#SidebarGst');
    this.selfPickupCheck = this.querySelector('#SidebarSelfPickup');
    this.shippingEl = this.querySelector('#SidebarShipping');
    this.totalEl = this.querySelector('#SidebarTotal');
    this.couponInput = this.querySelector('#SidebarCouponInput');
    this.couponApplyBtn = this.querySelector('#SidebarCouponApplyBtn');
    this.couponMsgEl = this.querySelector('#SidebarCouponMessage');
    this.notesInput = this.querySelector('#SidebarNotesInput');
    this.checkoutBtn = this.querySelector('#SidebarCheckoutBtn');

    // Quote settings (see the quote model note at the top of this file).
    this.setupFee        = numSetting(this.dataset.setupFee, 100);         // Rs per part
    this.pricePerGram    = numSetting(this.dataset.pricePerGram, 2.5);     // Rs per g of filament
    this.materialDensity = numSetting(this.dataset.density, 1.24);         // PLA, g/cm3
    this.machineRate     = numSetting(this.dataset.machineRate, 5.0);      // Rs per cm3 of machine time
    this.minItemTotal    = numSetting(this.dataset.minItemTotal, 200);     // Rs, minimum per part

    // Storefront figures that must not drift from the Shopify configuration.
    this.gstRate      = numSetting(this.dataset.gstRate, 18);       // %, prices are tax-inclusive
    this.flatShipping = numSetting(this.dataset.flatShipping, 100); // Rs, flat domestic rate

    // What the printers can actually make, in mm.
    this.buildVolume = {
      x: numSetting(this.dataset.buildX, 220),
      y: numSetting(this.dataset.buildY, 220),
      z: numSetting(this.dataset.buildZ, 250)
    };

    this.productPrice = numSetting(this.dataset.productPrice, 4.99);
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
  }

  generateOrderId() {
    const randomNum = Math.floor(100000000 + Math.random() * 900000000);
    if (this.orderIdEl) {
      this.orderIdEl.textContent = `3DPS-${randomNum}`;
    }
  }

  bindEvents() {
    // File upload events
    if (this.fileInput) {
      this.fileInput.addEventListener('change', (e) => this.handleFileSelect(e.target.files));
    }
    
    // Drag & drop
    if (this.dropzone) {
      ['dragenter', 'dragover'].forEach(name => {
        this.dropzone.addEventListener(name, (e) => {
          e.preventDefault();
          this.dropzone.classList.add('drag-over');
        });
      });

      ['dragleave', 'drop'].forEach(name => {
        this.dropzone.addEventListener(name, (e) => {
          e.preventDefault();
          this.dropzone.classList.remove('drag-over');
        });
      });

      this.dropzone.addEventListener('drop', (e) => {
        const files = e.dataTransfer.files;
        this.handleFileSelect(files);
      });
    }

    // Add More button triggers file input
    if (this.addMoreBtn) {
      this.addMoreBtn.addEventListener('click', () => {
        if (this.fileInput) this.fileInput.click();
      });
    }

    // View mode buttons
    this.viewModeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.viewMode;
        this.activeViewModes[mode] = !this.activeViewModes[mode];
        btn.classList.toggle('active', this.activeViewModes[mode]);
        this.updateViewModes();
      });
    });

    // Unit toggle buttons
    this.unitButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.unitButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.displayUnit = btn.dataset.unit;
        this.updateActiveViewerMetadata();
        this.renderTable();
      });
    });

    // Reset view camera
    if (this.resetViewBtn) {
      this.resetViewBtn.addEventListener('click', () => {
        if (this.modelFiles.length > 0 && this.modelFiles[this.activeModelIndex]) {
          const model = this.modelFiles[this.activeModelIndex];
          this.adjustCamera(model.mesh.geometry.boundingBox || model.mesh.geometry);
        }
      });
    }

    // Pagination controls
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        if (this.activeModelIndex > 0) {
          this.setActiveModel(this.activeModelIndex - 1);
        }
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        if (this.activeModelIndex < this.modelFiles.length - 1) {
          this.setActiveModel(this.activeModelIndex + 1);
        }
      });
    }

    // Shipping Checkbox
    if (this.selfPickupCheck) {
      // updateSidebar owns the shipping figure. This handler used to write Rs.150 here
      // while updateSidebar used Rs.100, so the two disagreed depending on what you
      // touched last, and neither was read from the delivery profile.
      this.selfPickupCheck.addEventListener('change', () => this.updateSidebar());
    }

    // Coupon Apply
    if (this.couponApplyBtn) {
      this.couponApplyBtn.addEventListener('click', () => this.applyCoupon());
    }



    // Checkout Button
    if (this.checkoutBtn) {
      this.checkoutBtn.addEventListener('click', () => this.checkoutCart());
    }

    this.onResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.onResize);
  }

  async loadLibraries() {
    if (this.threeLoaded) return;
    this.showLoader(true, 'Loading 3D Engine...');
    try {
      this.THREE = await import('https://esm.sh/three@0.150.0');
      const controlsMod = await import('https://esm.sh/three@0.150.0/examples/jsm/controls/OrbitControls.js');
      this.OrbitControls = controlsMod.OrbitControls;
      const stlMod = await import('https://esm.sh/three@0.150.0/examples/jsm/loaders/STLLoader.js');
      this.STLLoader = stlMod.STLLoader;
      const objMod = await import('https://esm.sh/three@0.150.0/examples/jsm/loaders/OBJLoader.js');
      this.OBJLoader = objMod.OBJLoader;
      this.threeLoaded = true;
      this.showLoader(false);
    } catch (err) {
      console.error('Failed to load WebGL engine:', err);
      this.showError('Could not load 3D preview library. Please check your internet connection.');
      this.showLoader(false);
      throw err;
    }
  }

  showLoader(show, text = 'Processing model...') {
    if (this.loaderElement) {
      this.loaderElement.classList.toggle('hidden', !show);
      const label = this.loaderElement.querySelector('.three-d-uploader__loader-text');
      if (label && text) label.textContent = text;
    }
  }

  showError(msg) {
    if (this.errorElement) {
      this.errorElement.textContent = msg;
      this.errorElement.classList.toggle('hidden', !msg);
    }
  }

  resetUploader() {
    this.modelFiles = [];
    this.activeModelIndex = 0;

    if (this.fileInput) this.fileInput.value = '';

    if (this.dropzone) this.dropzone.classList.remove('hidden');
    if (this.viewerContainer) this.viewerContainer.classList.add('hidden');
    if (this.addMoreBtn) this.addMoreBtn.style.display = 'none';

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    
    if (this.scene) {
      // Clear scene of model meshes
      const toRemove = [];
      this.scene.traverse(child => {
        if (child.isMesh && child !== this.gridHelper && child !== this.buildVolumeBox) {
          toRemove.push(child);
        }
      });
      toRemove.forEach(mesh => this.scene.remove(mesh));
    }

    this.renderTable();
    this.updateSidebar();
    this.showError('');
  }

  async handleFileSelect(files) {
    if (!files || files.length === 0) return;
    
    try {
      await this.loadLibraries();
      this.showLoader(true, 'Initializing ZIP / model parser...');
      
      const newFiles = Array.from(files);
      const JSZipModule = await import('https://esm.sh/jszip@3.10.1');
      const JSZip = JSZipModule.default;

      for (const file of newFiles) {
        const extension = file.name.split('.').pop().toLowerCase();
        
        if (extension === 'zip') {
          this.showLoader(true, `Decompressing ${file.name}...`);
          const zip = await JSZip.loadAsync(file);
          const entries = [];
          
          zip.forEach((path, entry) => {
            if (!entry.dir && (path.toLowerCase().endsWith('.stl') || path.toLowerCase().endsWith('.obj'))) {
              entries.push(entry);
            }
          });
          
          if (entries.length === 0) {
            this.showError('No STL or OBJ files found inside the ZIP.');
            continue;
          }

          for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            const entryName = entry.name.split('/').pop();
            const entryExt = entryName.split('.').pop().toLowerCase();
            this.showLoader(true, `Extracting ${entryName} (${i + 1}/${entries.length})...`);
            
            if (entryExt === 'stl') {
              const buffer = await entry.async('arraybuffer');
              await this.addModelFile(entryName, 'stl', buffer);
            } else if (entryExt === 'obj') {
              const text = await entry.async('string');
              await this.addModelFile(entryName, 'obj', text);
            }
          }
        } else if (extension === 'stl') {
          const buffer = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = (e) => resolve(e.target.result);
            r.onerror = reject;
            r.readAsArrayBuffer(file);
          });
          await this.addModelFile(file.name, 'stl', buffer);
        } else if (extension === 'obj') {
          const text = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = (e) => resolve(e.target.result);
            r.onerror = reject;
            r.readAsText(file);
          });
          await this.addModelFile(file.name, 'obj', text);
        } else {
          this.showError(`Unsupported file extension: .${extension}`);
        }
      }

      this.showLoader(false);

      if (this.modelFiles.length > 0) {
        if (this.dropzone) this.dropzone.classList.add('hidden');
        if (this.viewerContainer) this.viewerContainer.classList.remove('hidden');
        if (this.addMoreBtn) this.addMoreBtn.style.display = 'block';

        this.initScene();
        this.setActiveModel(this.modelFiles.length - 1);
        this.renderTable();
        this.updateSidebar();
      }

    } catch (err) {
      console.error(err);
      this.showError('Error parsing models.');
      this.showLoader(false);
    }
  }

  async addModelFile(name, type, data) {
    let geometry = null;
    let fileObj = null;

    const material = new this.THREE.MeshStandardMaterial({
      color: new this.THREE.Color('#ffffff'), // Default to white
      roughness: 0.25,
      metalness: 0.1,
      flatShading: true,
      side: this.THREE.DoubleSide
    });

    if (type === 'stl') {
      const loader = new this.STLLoader();
      geometry = loader.parse(data);
      geometry.center();
      
      const blob = new Blob([data], { type: 'application/octet-stream' });
      fileObj = new File([blob], name, { type: 'application/octet-stream' });
    } else if (type === 'obj') {
      const loader = new this.OBJLoader();
      const obj = loader.parse(data);
      
      // Merge OBJ meshes or grab first
      let objGeom = null;
      obj.traverse(child => {
        if (child.isMesh && !objGeom) {
          objGeom = child.geometry.clone();
        }
      });

      if (!objGeom) throw new Error('No meshes found in OBJ');
      geometry = objGeom;
      geometry.center();

      const blob = new Blob([data], { type: 'text/plain' });
      fileObj = new File([blob], name, { type: 'text/plain' });
    }

    geometry.computeBoundingBox();
    const size = new this.THREE.Vector3();
    geometry.boundingBox.getSize(size);

    const volumeMm3 = this.calculateSTLVolume(geometry);
    const volumeCm3 = volumeMm3 / 1000;
    
    // Triangle calculation
    const triangleCount = geometry.index ? geometry.index.count / 3 : geometry.attributes.position.count / 3;

    const mesh = new this.THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.set(0, size.y / 2, 0);

    const modelEntry = {
      name: name,
      // STL/OBJ have no units. rawVolume/rawSize are the file's own numbers; volume and
      // size are those numbers interpreted in sourceUnit, and are what we quote on.
      sourceUnit: 'mm',
      // Resize factor the shopper applies on top of the file's own size. 1 = as uploaded,
      // and rawVolume/rawSize are never written to, so Reset size is always exact.
      scale: 1,
      rawVolume: volumeCm3,
      rawSize: size.clone(),
      volume: volumeCm3,
      size: size,
      triangles: triangleCount,
      file: fileObj,
      mesh: mesh,
      material: material,
      technology: 'FDM/FFF',
      materialType: 'PLA',
      colorName: 'White',
      quality: 'Standard',
      density: '20%',
      quantity: 1,
      price: 0,
      total: 0,
      colorPickerOpen: false,
      weightGrams: 0,
      warnings: []
    };

    // Interprets the file as mm at 100%, prices it, and runs the shop-floor checks.
    this.applyGeometry(modelEntry);
    this.modelFiles.push(modelEntry);
  }

  calculateSTLVolume(geometry) {
    let volume = 0;
    const position = geometry.attributes.position;
    const index = geometry.index;
    if (!position) return 0;

    const vA = new this.THREE.Vector3();
    const vB = new this.THREE.Vector3();
    const vC = new this.THREE.Vector3();
    const tempCross = new this.THREE.Vector3();

    if (index) {
      for (let i = 0; i < index.count; i += 3) {
        vA.fromBufferAttribute(position, index.getX(i));
        vB.fromBufferAttribute(position, index.getX(i + 1));
        vC.fromBufferAttribute(position, index.getX(i + 2));
        tempCross.crossVectors(vB, vC);
        volume += vA.dot(tempCross) / 6.0;
      }
    } else {
      for (let i = 0; i < position.count; i += 3) {
        vA.fromBufferAttribute(position, i);
        vB.fromBufferAttribute(position, i + 1);
        vC.fromBufferAttribute(position, i + 2);
        tempCross.crossVectors(vB, vC);
        volume += vA.dot(tempCross) / 6.0;
      }
    }
    return Math.abs(volume);
  }

  calculateModelPrice(model) {
    // Machine time: how big the part is and how fine the layers are. Independent of infill.
    const qualityFactor = QUALITY_TIME_FACTOR[model.quality] || 1.0;
    const machineCost = model.volume * this.machineRate * qualityFactor;

    // Plastic actually extruded. Shells are solid whatever the infill; infill fills the
    // rest. Priced by weight, which is how filament is bought and how a job is planned.
    const infillFraction = Math.min(1, Math.max(0, (parseInt(model.density) || 20) / 100));
    const solidFraction = SHELL_FRACTION + (1 - SHELL_FRACTION) * infillFraction;
    const materialGrams = model.volume * solidFraction * this.materialDensity;
    const materialCost = materialGrams * this.pricePerGram;
    model.weightGrams = materialGrams;

    // ONE unit price, so the Price column and the Item Total column cannot disagree.
    const itemUnitPrice = Math.max(this.minItemTotal, this.setupFee + materialCost + machineCost);
    const quantity = Math.max(1, parseInt(model.quantity) || 1);
    const rawTotal = quantity * itemUnitPrice;

    // Checkout charges variantPrice x lineQuantity, so the line has to land on a whole
    // number of variant-price units. Round the LINE (not each piece) UP - rounding down
    // undercharges - then derive the displayed figures back out of the rounded result so
    // the page can never show a total that checkout will not match.
    const shopifyQty = Math.max(1, Math.ceil(rawTotal / this.productPrice));
    model.shopifyQty = shopifyQty;
    model.total = shopifyQty * this.productPrice;
    model.price = model.total / quantity;
  }

  /**
   * STL and OBJ files carry no unit information - a slicer simply assumes millimetres.
   * A part exported in inches therefore reads as 1/25.4 of its real size and quotes at
   * about 1/16000 of the right price. This lets the shopper correct the file's units.
   */
  /**
   * Single place where a model's real-world geometry is derived. Two independent
   * factors multiply together:
   *   sourceUnit - what the file's numbers mean (mm / cm / inch)
   *   scale      - how much bigger or smaller the shopper wants it printed
   * rawSize and rawVolume are never mutated, so Reset size restores exactly the
   * geometry that came out of the file, with no drift from repeated scaling.
   */
  applyGeometry(model) {
    const unitFactor = UNIT_SCALE[model.sourceUnit] || 1;
    const factor = unitFactor * (model.scale || 1);

    model.size = model.rawSize.clone().multiplyScalar(factor);
    // Volume is three-dimensional: doubling a model's size makes it EIGHT times the
    // plastic, not twice. This is why resizing has to re-price, not just re-label.
    model.volume = model.rawVolume * Math.pow(factor, 3);

    if (model.mesh) {
      model.mesh.scale.setScalar(factor);
      model.mesh.position.set(0, model.size.y / 2, 0);
    }

    this.calculateModelPrice(model);
    this.assessModel(model);
  }

  applySourceUnit(model, unit) {
    model.sourceUnit = UNIT_SCALE[unit] ? unit : 'mm';
    this.applyGeometry(model);
  }

  /** Resize as a percentage of the uploaded size. Clamped to the printable range. */
  applyScale(model, percent) {
    let pct = parseFloat(percent);
    if (!Number.isFinite(pct)) pct = 100;
    pct = Math.min(SCALE_MAX_PCT, Math.max(SCALE_MIN_PCT, pct));
    model.scale = pct / 100;
    this.applyGeometry(model);
    return pct;
  }

  /** Restore the model to exactly the size it was uploaded at. */
  resetSize(model) {
    model.scale = 1;
    this.applyGeometry(model);
  }

  /**
   * Redraw everything that depends on a model's geometry after a resize: the table row,
   * the running totals, and - if this is the part currently in the viewer - the readout
   * and the camera framing.
   */
  refreshAfterGeometryChange(idx) {
    if (idx === this.activeModelIndex) {
      const model = this.modelFiles[idx];
      this.updateActiveViewerMetadata();
      if (model && model.mesh && model.mesh.geometry) {
        geometryBoundingBoxCheck(model.mesh.geometry);
        this.adjustCamera(model.mesh.geometry.boundingBox || model.mesh.geometry);
      }
    }
    this.renderTable();
    this.updateSidebar();
  }

  /**
   * Shop-floor sanity checks: is the file in the units we think it is, and can the
   * printers actually make this part?
   */
  assessModel(model) {
    const warnings = [];
    const s = model.size;
    const maxDim = Math.max(s.x, s.y, s.z);

    if (model.sourceUnit === 'mm' && maxDim > 0 && maxDim < SUSPICIOUS_MAX_DIM_MM) {
      warnings.push(`Largest dimension is only ${maxDim.toFixed(1)} mm. This file was most likely exported in cm or inches - set the correct units below.`);
    }

    // Parts print one per plate, so each must fit the build volume on its own. The part
    // can be rotated flat onto the plate, so compare footprints regardless of X/Y order.
    const bv = this.buildVolume;
    const footprint = [s.x, s.y].sort((a, b) => a - b);
    const plate = [bv.x, bv.y].sort((a, b) => a - b);
    if (footprint[0] > plate[0] || footprint[1] > plate[1] || s.z > bv.z) {
      warnings.push(`Larger than our ${bv.x} x ${bv.y} x ${bv.z} mm build volume. This part has to be scaled down or split before we can print it.`);
    }

    model.warnings = warnings;
    return warnings;
  }

  /** A part that will not fit the plate must not reach checkout. */
  blockingWarnings() {
    return this.modelFiles
      .filter(m => (m.warnings || []).some(w => w.indexOf('build volume') !== -1))
      .map(m => m.name);
  }

  setActiveModel(index) {
    if (index < 0 || index >= this.modelFiles.length) return;
    
    // Remove previous mesh from scene
    if (this.modelFiles[this.activeModelIndex] && this.modelFiles[this.activeModelIndex].mesh) {
      this.scene.remove(this.modelFiles[this.activeModelIndex].mesh);
    }

    this.activeModelIndex = index;
    const model = this.modelFiles[index];

    // Add active mesh to scene
    this.scene.add(model.mesh);
    this.updateViewModes();

    // Reset camera target
    geometryBoundingBoxCheck(model.mesh.geometry);
    this.adjustCamera(model.mesh.geometry.boundingBox || model.mesh.geometry);

    // Update active filename & stats
    if (this.viewerFilename) this.viewerFilename.textContent = model.name;
    this.updateActiveViewerMetadata();
    this.renderPagination();
  }

  updateActiveViewerMetadata() {
    if (this.modelFiles.length === 0 || !this.modelFiles[this.activeModelIndex]) return;
    
    const model = this.modelFiles[this.activeModelIndex];
    const size = model.size;

    if (this.displayUnit === 'mm') {
      if (this.viewerDimensions) this.viewerDimensions.textContent = `${size.x.toFixed(2)} × ${size.y.toFixed(2)} × ${size.z.toFixed(2)} mm`;
      if (this.viewerVolume) this.viewerVolume.textContent = `${model.volume.toFixed(2)} cm³`;
    } else {
      const inX = size.x / 25.4;
      const inY = size.y / 25.4;
      const inZ = size.z / 25.4;
      const volInch = model.volume / 16.387;
      if (this.viewerDimensions) this.viewerDimensions.textContent = `${inX.toFixed(2)} × ${inY.toFixed(2)} × ${inZ.toFixed(2)} inch`;
      if (this.viewerVolume) this.viewerVolume.textContent = `${volInch.toFixed(2)} inch³`;
    }

    if (this.viewerTriangles) {
      const formattedTriangles = new Intl.NumberFormat('en-US').format(Math.round(model.triangles));
      this.viewerTriangles.textContent = `${formattedTriangles} triangles`;
    }

    // Envelope warning check
    const isExceeded = size.x > 180 || size.y > 180 || size.z > 180;
    if (isExceeded) {
      if (this.buildVolumeBox) this.buildVolumeBox.material.color.setHex(0xff3b30); // Red
      this.showError('Warning: Selected model exceeds the maximum build envelope of 180x180x180 mm.');
    } else {
      if (this.buildVolumeBox) this.buildVolumeBox.material.color.setHex(0x007aff); // Blue
      this.showError('');
    }
  }

  renderPagination() {
    if (!this.pageNumbersContainer) return;
    this.pageNumbersContainer.innerHTML = '';
    
    if (this.prevBtn) this.prevBtn.disabled = this.activeModelIndex === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.activeModelIndex === this.modelFiles.length - 1;

    for (let i = 0; i < this.modelFiles.length; i++) {
      const pageNum = document.createElement('span');
      pageNum.className = `pagination-number${i === this.activeModelIndex ? ' active' : ''}`;
      pageNum.textContent = i + 1;
      pageNum.addEventListener('click', () => this.setActiveModel(i));
      this.pageNumbersContainer.appendChild(pageNum);
    }
  }

  removeModelAtIndex(index) {
    if (index < 0 || index >= this.modelFiles.length) return;
    
    const model = this.modelFiles[index];
    if (this.scene && model.mesh) {
      this.scene.remove(model.mesh);
    }

    this.modelFiles.splice(index, 1);
    
    if (this.modelFiles.length === 0) {
      this.resetUploader();
      return;
    }

    let nextIndex = this.activeModelIndex;
    if (nextIndex >= this.modelFiles.length) {
      nextIndex = this.modelFiles.length - 1;
    }
    
    this.setActiveModel(nextIndex);
    this.renderTable();
    this.updateSidebar();
  }

  renderTable() {
    if (!this.tableBody) return;
    
    if (this.modelFiles.length === 0) {
      this.tableBody.innerHTML = `
        <tr class="empty-table-row">
          <td colspan="9" style="text-align: center; padding: 2.5rem; color: rgb(var(--color-foreground-rgb) / 0.5); font-size: 1.2rem;">
            No files uploaded yet. Please upload your 3D files in Step 1.
          </td>
        </tr>
      `;
      return;
    }

    const colorMap = {
      'White': '#ffffff',
      'Grey': '#8e8e93',
      'Black': '#1c1c1e',
      'Red': '#ff3b30',
      'Green': '#4cd964',
      'Blue': '#007aff',
      'Yellow': '#ffcc00',
      'Pink': '#ffb6c1'
    };

    let html = '';
    this.modelFiles.forEach((model, index) => {
      const activeColorName = model.colorName || 'White';
      const activeColorHex = colorMap[activeColorName] || '#ffffff';
      
      const sizeText = this.displayUnit === 'mm' 
        ? `${model.size.x.toFixed(0)} × ${model.size.y.toFixed(0)} × ${model.size.z.toFixed(0)} mm`
        : `${(model.size.x/25.4).toFixed(1)} × ${(model.size.y/25.4).toFixed(1)} × ${(model.size.z/25.4).toFixed(1)} inch`;
      
      const volText = this.displayUnit === 'mm'
        ? `${model.volume.toFixed(1)} cm³`
        : `${(model.volume/16.387).toFixed(1)} in³`;

      // Resize state. origText is the file's own size, shown only while resized so the
      // shopper can see what Reset size would take them back to.
      const scalePct = Math.round((model.scale || 1) * 100);
      const unitFactor = UNIT_SCALE[model.sourceUnit] || 1;
      const o = model.rawSize.clone().multiplyScalar(unitFactor);
      const origText = this.displayUnit === 'mm'
        ? `${o.x.toFixed(0)} × ${o.y.toFixed(0)} × ${o.z.toFixed(0)} mm`
        : `${(o.x/25.4).toFixed(1)} × ${(o.y/25.4).toFixed(1)} × ${(o.z/25.4).toFixed(1)} inch`;

      html += `
        <tr data-index="${index}" style="${index === this.activeModelIndex ? 'background: rgba(var(--primary-neon-rgb), 0.02);' : ''}">
          <td>${index + 1}</td>
          <td>
            <div class="table-model-name" title="${model.name}" style="cursor: pointer; color: ${index === this.activeModelIndex ? 'var(--primary-neon)' : 'inherit'}; font-weight: ${index === this.activeModelIndex ? '700' : '500'};">${model.name}</div>
            <div class="table-model-stats">${sizeText} &bull; ${volText} &bull; ~${model.weightGrams.toFixed(0)} g</div>
            ${scalePct !== 100 ? `<div class="table-model-original">Original size: ${origText}</div>` : ''}
            <div class="table-model-controls">
              <label class="table-unit-label">File units
                <select class="table-select table-unit-select" data-setting="sourceUnit">
                  <option value="mm" ${model.sourceUnit === 'mm' ? 'selected' : ''}>mm</option>
                  <option value="cm" ${model.sourceUnit === 'cm' ? 'selected' : ''}>cm</option>
                  <option value="inch" ${model.sourceUnit === 'inch' ? 'selected' : ''}>inch</option>
                </select>
              </label>
              <div class="table-scale-group" role="group" aria-label="Resize ${model.name}">
                <span class="table-scale-caption">Size</span>
                <button type="button" class="table-scale-btn" data-scale-step="-${SCALE_STEP_PCT}" aria-label="Make smaller" ${scalePct <= SCALE_MIN_PCT ? 'disabled' : ''}>&minus;</button>
                <input type="number" class="table-scale-input" value="${scalePct}" min="${SCALE_MIN_PCT}" max="${SCALE_MAX_PCT}" step="${SCALE_STEP_PCT}" aria-label="Size as a percentage of the uploaded model">
                <span class="table-scale-pct">%</span>
                <button type="button" class="table-scale-btn" data-scale-step="${SCALE_STEP_PCT}" aria-label="Make larger" ${scalePct >= SCALE_MAX_PCT ? 'disabled' : ''}>+</button>
                <button type="button" class="table-scale-reset" ${scalePct === 100 ? 'disabled' : ''}>Reset size</button>
              </div>
            </div>
            ${(model.warnings || []).map(w => `<div class="table-model-warning">${w}</div>`).join('')}
          </td>
          <td>
            <div class="table-color-dropdown-wrapper">
              <button type="button" class="table-color-btn" data-trigger-color="${index}">
                <span class="table-color-swatch-indicator" style="background-color: ${activeColorHex};"></span>
                <span>${activeColorName}</span>
              </button>
              ${model.colorPickerOpen ? `
                <div class="table-color-dropdown-menu">
                  ${Object.entries(colorMap).map(([colorName, colorHex]) => `
                    <button type="button" class="table-color-option${activeColorName === colorName ? ' active' : ''}" 
                            data-color-name="${colorName}" 
                            data-color-hex="${colorHex}"
                            style="background-color: ${colorHex};"
                            title="${colorName}"
                            aria-label="${colorName}">
                    </button>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </td>
          <td>
            <select class="table-select" data-setting="quality">
              <option value="Standard" ${model.quality === 'Standard' ? 'selected' : ''}>Standard</option>
              <option value="High" ${model.quality === 'High' ? 'selected' : ''}>High</option>
              <option value="Ultra" ${model.quality === 'Ultra' ? 'selected' : ''}>Ultra</option>
            </select>
          </td>
          <td>
            <select class="table-select" data-setting="density">
              <option value="10%" ${model.density === '10%' ? 'selected' : ''}>10%</option>
              <option value="20%" ${model.density === '20%' ? 'selected' : ''}>20%</option>
              <option value="30%" ${model.density === '30%' ? 'selected' : ''}>30%</option>
              <option value="40%" ${model.density === '40%' ? 'selected' : ''}>40%</option>
              <option value="50%" ${model.density === '50%' ? 'selected' : ''}>50%</option>
              <option value="60%" ${model.density === '60%' ? 'selected' : ''}>60%</option>
              <option value="70%" ${model.density === '70%' ? 'selected' : ''}>70%</option>
              <option value="80%" ${model.density === '80%' ? 'selected' : ''}>80%</option>
              <option value="90%" ${model.density === '90%' ? 'selected' : ''}>90%</option>
              <option value="100%" ${model.density === '100%' ? 'selected' : ''}>100%</option>
            </select>
          </td>
          <td>
            <input type="number" class="table-qty-input" value="${model.quantity}" min="1" max="999" data-setting="quantity">
          </td>
          <td style="text-align: right;" class="table-price">₹${model.price.toFixed(2)}</td>
          <td style="text-align: right;" class="table-item-total">₹${model.total.toFixed(2)}</td>
          <td>
            <button type="button" class="table-remove-btn" title="Remove part" data-remove="${index}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
          </td>
        </tr>
      `;
    });

    this.tableBody.innerHTML = html;

    // Table Event Listeners
    // Row clicks to activate viewer
    const rows = this.tableBody.querySelectorAll('tr');
    rows.forEach(row => {
      const nameCol = row.querySelector('.table-model-name');
      if (nameCol) {
        nameCol.addEventListener('click', () => {
          const idx = parseInt(row.dataset.index);
          this.setActiveModel(idx);
          this.renderTable();
        });
      }
    });

    // Dropdown updates
    const selects = this.tableBody.querySelectorAll('.table-select');
    selects.forEach(select => {
      select.addEventListener('change', (e) => {
        const row = select.closest('tr');
        const idx = parseInt(row.dataset.index);
        const setting = select.dataset.setting;
        const value = e.target.value;

        if (setting === 'sourceUnit') {
          // Not just a field: rescales the geometry, the volume, the weight and the price.
          this.applySourceUnit(this.modelFiles[idx], value);
          this.refreshAfterGeometryChange(idx);
          return;
        } else {
          // Auto-change material options if technology changes
          if (setting === 'technology') {
            this.modelFiles[idx].technology = value;
            this.modelFiles[idx].materialType = value === 'SLA (Resin)' ? 'Resin (SLA)' : 'PLA';
          } else {
            this.modelFiles[idx][setting] = value;
          }
          this.calculateModelPrice(this.modelFiles[idx]);
          this.assessModel(this.modelFiles[idx]);
        }

        this.renderTable();
        this.updateSidebar();
      });
    });

    // Quantity updates
    const qtyInputs = this.tableBody.querySelectorAll('.table-qty-input');
    qtyInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const row = input.closest('tr');
        const idx = parseInt(row.dataset.index);
        let val = parseInt(e.target.value);
        if (isNaN(val) || val < 1) val = 1;
        
        this.modelFiles[idx].quantity = val;
        this.calculateModelPrice(this.modelFiles[idx]);
        this.renderTable();
        this.updateSidebar();
      });

      input.addEventListener('keyup', (e) => {
        if (e.key === 'Enter') input.blur();
      });
    });

    // Resize: typed percentage
    this.tableBody.querySelectorAll('.table-scale-input').forEach(input => {
      const commit = (e) => {
        const row = input.closest('tr');
        const idx = parseInt(row.dataset.index);
        this.applyScale(this.modelFiles[idx], e.target.value);
        this.refreshAfterGeometryChange(idx);
      };
      input.addEventListener('change', commit);
      input.addEventListener('keyup', (e) => {
        if (e.key === 'Enter') input.blur();
      });
    });

    // Resize: - / + steppers
    this.tableBody.querySelectorAll('[data-scale-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        const row = btn.closest('tr');
        const idx = parseInt(row.dataset.index);
        const model = this.modelFiles[idx];
        const current = Math.round((model.scale || 1) * 100);
        this.applyScale(model, current + parseFloat(btn.dataset.scaleStep));
        this.refreshAfterGeometryChange(idx);
      });
    });

    // Resize: back to the size the file was uploaded at
    this.tableBody.querySelectorAll('.table-scale-reset').forEach(btn => {
      btn.addEventListener('click', () => {
        const row = btn.closest('tr');
        const idx = parseInt(row.dataset.index);
        this.resetSize(this.modelFiles[idx]);
        this.refreshAfterGeometryChange(idx);
      });
    });

    // Color buttons toggle custom dropdown menu
    const colorBtns = this.tableBody.querySelectorAll('[data-trigger-color]');
    colorBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const row = btn.closest('tr');
        const idx = parseInt(row.dataset.index);
        
        // Close all other color menus
        this.modelFiles.forEach((m, i) => {
          if (i !== idx) m.colorPickerOpen = false;
        });
        
        this.modelFiles[idx].colorPickerOpen = !this.modelFiles[idx].colorPickerOpen;
        this.renderTable();
      });
    });

    // Color options select handler
    const colorOpts = this.tableBody.querySelectorAll('.table-color-option');
    colorOpts.forEach(opt => {
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        const row = opt.closest('tr');
        const idx = parseInt(row.dataset.index);
        const colorName = opt.dataset.colorName;
        const colorHex = opt.dataset.colorHex;

        this.modelFiles[idx].colorName = colorName;
        this.modelFiles[idx].colorPickerOpen = false;
        
        // Update Three.js mesh material color
        if (this.modelFiles[idx].material) {
          this.modelFiles[idx].material.color.set(colorHex);
        }

        this.renderTable();
      });
    });

    // Close color picker on window click
    document.addEventListener('click', () => {
      let openMenu = false;
      this.modelFiles.forEach(m => {
        if (m.colorPickerOpen) {
          m.colorPickerOpen = false;
          openMenu = true;
        }
      });
      if (openMenu) this.renderTable();
    }, { once: true });

    // Remove buttons
    const removeBtns = this.tableBody.querySelectorAll('[data-remove]');
    removeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.remove);
        this.removeModelAtIndex(idx);
      });
    });
  }

  applyCoupon() {
    if (!this.couponInput) return;
    const code = this.couponInput.value.trim().toUpperCase();
    this.couponMsgEl.classList.remove('hidden', 'success', 'error');

    if (code === '') {
      this.discountPercent = 0;
      this.couponCode = '';
      this.couponMsgEl.classList.add('hidden');
      this.updateSidebar();
      return;
    }

    // The theme cannot validate a discount code or know its value - only Shopify can.
    // This used to hard-code FIRST10/FREE50 and subtract the percentage from the DISPLAYED
    // total while checkout charged full price. Neither code has ever existed in this store.
    // Now the code is carried through to checkout and Shopify decides whether it applies.
    this.discountPercent = 0;
    this.couponCode = code;
    this.couponMsgEl.textContent = `Code "${code}" will be applied at checkout if valid. The total below is before any discount.`;
    this.couponMsgEl.className = 'coupon-msg success';

    this.updateSidebar();
  }

  updateSidebar() {
    if (this.modelFiles.length === 0) {
      if (this.totalFilesEl) this.totalFilesEl.textContent = '0';
      if (this.leadTimeEl) this.leadTimeEl.textContent = '1 Day';
      if (this.subtotalEl) this.subtotalEl.textContent = '₹0.00';
      if (this.gstEl) this.gstEl.textContent = '₹0.00';
      if (this.totalEl) this.totalEl.textContent = '₹0.00';
      if (this.checkoutBtn) {
        this.checkoutBtn.disabled = true;
        this.checkoutBtn.textContent = 'Checkout';
      }
      this.showError('');
      return;
    }

    if (this.checkoutBtn) this.checkoutBtn.disabled = false;

    // Total files count
    let totalCount = 0;
    this.modelFiles.forEach(m => totalCount += m.quantity);
    if (this.totalFilesEl) this.totalFilesEl.textContent = totalCount;

    // Subtotal
    let subtotal = 0;
    this.modelFiles.forEach(m => subtotal += m.total);

    // Apply Coupon discount
    let discount = 0;
    if (this.discountPercent > 0) {
      discount = subtotal * (this.discountPercent / 100);
      subtotal = subtotal - discount;
    }

    // Lead Time estimation
    let leadTime = '1 Day';
    if (totalCount > 10 || subtotal > 5000) leadTime = '2 Days';
    if (totalCount > 30 || subtotal > 15000) leadTime = '3 - 5 Days';
    if (this.leadTimeEl) this.leadTimeEl.textContent = leadTime;

    // GST. This shop prices tax-inclusive (Shopify shop.taxesIncluded = true), so this
    // is the tax already contained in the subtotal, not an addition to it. The rate comes
    // from theme settings so it cannot silently drift from what Shopify actually charges.
    const gst = subtotal - (subtotal / (1 + this.gstRate / 100));

    // Shipping mirrors the flat domestic rate on the delivery profile. Anything else -
    // other zones, or a rate change - is settled by Shopify at checkout.
    const isSelfPickup = this.selfPickupCheck ? this.selfPickupCheck.checked : false;
    const shipping = isSelfPickup ? 0 : this.flatShipping;

    // Grand Total (since GST is inclusive, total is subtotal + shipping)
    const total = subtotal + shipping;

    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    });

    if (this.subtotalEl) this.subtotalEl.textContent = formatter.format(subtotal);
    if (this.gstEl) this.gstEl.textContent = formatter.format(gst);
    if (this.shippingEl) this.shippingEl.textContent = isSelfPickup ? 'Free' : formatter.format(shipping);
    if (this.totalEl) this.totalEl.textContent = formatter.format(total);

    // A part that will not fit the plate cannot be accepted, however it is priced.
    const unprintable = this.blockingWarnings();
    if (this.checkoutBtn) {
      this.checkoutBtn.disabled = unprintable.length > 0;
      this.checkoutBtn.textContent = unprintable.length > 0 ? 'Part too large to print' : 'Checkout';
    }
    this.showError(unprintable.length > 0
      ? `Cannot print: ${unprintable.join(', ')}. Scale the part down or split it, then re-upload.`
      : '');
  }

  async checkoutCart() {
    this.showLoader(true, 'Initiating secure checkout...');
    this.showError('');

    try {
      // Remove only THIS uploader's previous quote lines. The old code called
      // /cart/clear.js, which also threw away any unrelated items the shopper had in
      // their cart. Clearing by variant keeps the double-add guard without the collateral.
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await cartRes.json();
        const stale = (cart.items || []).filter(i => String(i.variant_id) === String(this.variantId));
        for (const line of stale) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
        }
      } catch (tidyErr) {
        // Not fatal: worst case the shopper sees an older quote line alongside the new one.
        console.warn('[3D uploader] could not remove previous quote lines', tidyErr);
      }

      // Create a hidden form to submit all items synchronously
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/cart/add';
      form.enctype = 'multipart/form-data';
      form.style.display = 'none';

      // Redirect directly to checkout after addition
      const returnToInput = document.createElement('input');
      returnToInput.type = 'hidden';
      returnToInput.name = 'return_to';
      // /discount/<CODE>?redirect=/checkout is Shopify's own discount-link form: it applies
      // the code (if it is real and eligible) and then forwards to checkout. If the code is
      // bad the shopper still reaches checkout, just without a discount.
      returnToInput.value = this.couponCode
        ? `/discount/${encodeURIComponent(this.couponCode)}?redirect=${encodeURIComponent('/checkout')}`
        : '/checkout';
      form.appendChild(returnToInput);

      const formatter = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
      });

      this.modelFiles.forEach((model, index) => {
        // Variant ID
        const idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = `items[${index}][id]`;
        idInput.value = this.variantId;
        form.appendChild(idInput);

        // Quantity
        const qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = `items[${index}][quantity]`;
        qtyInput.value = model.shopifyQty.toString();
        form.appendChild(qtyInput);

        // File property
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.name = `items[${index}][properties][Uploaded Model File]`;
        fileInput.style.display = 'none';
        
        const dt = new DataTransfer();
        dt.items.add(model.file);
        fileInput.files = dt.files;
        form.appendChild(fileInput);

        // Additional properties
        const estPriceStr = formatter.format(model.total);
        const props = {
          // The Shopify line quantity below is a PRICE unit count, not a piece count.
          // Production reads this property for how many physical prints to make.
          'Pieces': String(Math.max(1, parseInt(model.quantity) || 1)),
          'Material': 'PLA',
          'Color': model.colorName,
          'Quality': model.quality,
          'Infill Density': model.density,
          'Dimensions': `${model.size.x.toFixed(1)} x ${model.size.y.toFixed(1)} x ${model.size.z.toFixed(1)} mm`,
          'File Units': model.sourceUnit,
          'Scale': `${Math.round((model.scale || 1) * 100)}% of uploaded size`,
          'Volume': `${model.volume.toFixed(2)} cm³`,
          'Est. Material': `${model.weightGrams.toFixed(0)} g`,
          'Estimated Cost': estPriceStr,
          'Pricing Unit': `${model.shopifyQty} x ${formatter.format(this.productPrice)} (line quantity is price, not pieces)`
        };

        if (this.couponCode) {
          props['Coupon Code'] = this.couponCode;
        }

        const notes = this.notesInput ? this.notesInput.value.trim() : '';
        if (notes) {
          props['Order Notes'] = notes;
        }

        if (this.selfPickupCheck && this.selfPickupCheck.checked) {
          props['Self Pickup'] = 'Yes';
        }

        for (const [key, val] of Object.entries(props)) {
          const propInput = document.createElement('input');
          propInput.type = 'hidden';
          propInput.name = `items[${index}][properties][${key}]`;
          propInput.value = val;
          form.appendChild(propInput);
        }
      });

      document.body.appendChild(form);
      form.submit();

    } catch (err) {
      console.error(err);
      this.showError(`Checkout failed: ${err.message}`);
      this.showLoader(false);
    }
  }

  // ----------------------------------------------------
  // WebGL Renderer Setup
  // ----------------------------------------------------

  initScene() {
    if (this.scene) {
      if (!this.animationId) {
        this.animate();
      }
      return;
    }

    const width = this.canvasContainer.clientWidth || 300;
    const height = this.canvasContainer.clientHeight || 300;

    this.scene = new this.THREE.Scene();
    this.scene.background = new this.THREE.Color(this.dataset.bgColor || '#f9f9fb');

    this.camera = new this.THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    this.camera.position.set(100, 100, 100);

    this.renderer = new this.THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;

    // Controls
    this.controls = new this.OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.maxPolarAngle = Math.PI / 2;

    // Lights
    const ambientLight = new this.THREE.AmbientLight(0xffffff, 0.65);
    this.scene.add(ambientLight);

    const dirLight1 = new this.THREE.DirectionalLight(0xffffff, 0.75);
    dirLight1.position.set(100, 150, 50);
    dirLight1.castShadow = true;
    this.scene.add(dirLight1);

    const dirLight2 = new this.THREE.DirectionalLight(0xaaccff, 0.35);
    dirLight2.position.set(-100, -50, -50);
    this.scene.add(dirLight2);

    // Grid Helper
    this.gridHelper = new this.THREE.GridHelper(200, 50, 0xd1d1d6, 0xe5e5ea);
    this.gridHelper.position.y = -0.01;
    this.scene.add(this.gridHelper);

    // Axes Helper
    this.axesHelper = new this.THREE.AxesHelper(150);
    this.axesHelper.position.set(0, 0.05, 0);
    this.scene.add(this.axesHelper);

    // Build Plate Boundary
    const boxGeom = new this.THREE.BoxGeometry(180, 180, 180);
    const edges = new this.THREE.EdgesGeometry(boxGeom);
    const lineMaterial = new this.THREE.LineBasicMaterial({ color: 0x007aff });
    this.buildVolumeBox = new this.THREE.LineSegments(edges, lineMaterial);
    this.buildVolumeBox.position.set(0, 90, 0);
    this.scene.add(this.buildVolumeBox);

    this.animate();
  }

  updateViewModes() {
    if (this.gridHelper) this.gridHelper.visible = this.activeViewModes.grid;
    if (this.axesHelper) this.axesHelper.visible = this.activeViewModes.axes;

    if (this.modelFiles.length > 0 && this.modelFiles[this.activeModelIndex]) {
      const model = this.modelFiles[this.activeModelIndex];
      if (model.material) {
        model.material.wireframe = this.activeViewModes.wireframe;
      }
    }
  }

  adjustCamera(boundingBoxOrGeometry) {
    let bbox = new this.THREE.Box3();
    if (boundingBoxOrGeometry.isBufferGeometry) {
      boundingBoxOrGeometry.computeBoundingBox();
      bbox.copy(boundingBoxOrGeometry.boundingBox);
    } else {
      bbox.copy(boundingBoxOrGeometry);
    }

    const size = new this.THREE.Vector3();
    bbox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    
    const fov = this.camera.fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
    cameraZ *= 1.4;

    const centerY = size.y / 2;
    this.camera.position.set(cameraZ, centerY + cameraZ, cameraZ);
    this.camera.lookAt(0, centerY, 0);

    this.controls.maxDistance = cameraZ * 5;
    this.controls.minDistance = maxDim * 0.2;
    this.controls.target.set(0, centerY, 0);
    this.controls.update();
  }

  animate() {
    this.animationId = requestAnimationFrame(() => this.animate());
    if (this.controls) this.controls.update();
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  handleResize() {
    if (!this.renderer || !this.canvasContainer) return;
    const width = this.canvasContainer.clientWidth;
    const height = this.canvasContainer.clientHeight;
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }
}

// Utility check to ensure boundingBox is computed
function geometryBoundingBoxCheck(geometry) {
  if (geometry && !geometry.boundingBox) {
    geometry.computeBoundingBox();
  }
}

if (!customElements.get('three-d-uploader')) {
  customElements.define('three-d-uploader', ThreeDUploader);
}
